import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Archive, ArrowLeft, User, Award,
  Calendar, Clock, Mail, Phone, MapPin, Building, Download, ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/employee-archives`;

const ArchiveDetailView = () => {
  const { archiveId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [validOnDate, setValidOnDate] = useState('');

  useEffect(() => {
    fetchArchive();
  }, [archiveId]);

  const fetchArchive = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/${archiveId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArchive(response.data);
    } catch (err) {
      console.error('Error fetching archive:', err);
      setError('Failed to fetch archive details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const tabs = [
    { id: 'personal', label: 'Personal Data', icon: User },
    { id: 'qualifications', label: 'Qualifications', icon: Award },
  ];

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex flex-col items-center justify-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-lg text-slate-700">{error}</p>
        <button
          onClick={() => navigate('/users/archives')}
          className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
        >
          Back to Archives
        </button>
      </div>
    );
  }

  if (!archive) return null;

  const userSnapshot = archive.user_snapshot || {};

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/users/archives')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-brand-100 rounded-full p-3">
              <Archive className="w-6 h-6 text-brand-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {archive.employee_name}
              </h1>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  {archive.company_name}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Archived: {formatDate(archive.archived_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Expires: {formatDate(archive.expires_at)}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  archive.is_expired ? 'bg-red-100 text-red-700' :
                  archive.days_until_expiry <= 90 ? 'bg-amber-100 text-amber-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {archive.is_expired ? 'Expired' : `${archive.days_until_expiry} days left`}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6">
        <div className="flex justify-between items-center">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          {activeTab === 'qualifications' && (
            <div className="flex items-center gap-2 py-2 mr-4">
              <label className="text-sm text-slate-600">Qualifications valid on:</label>
              <input
                type="date"
                value={validOnDate}
                onChange={(e) => setValidOnDate(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              {validOnDate && (
                <button
                  onClick={() => setValidOnDate('')}
                  className="text-slate-400 hover:text-slate-600 text-sm"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 p-6 ${activeTab === 'qualifications' ? 'flex flex-col min-h-0' : 'overflow-y-auto'}`}>
        {activeTab === 'personal' && (
          <PersonalDataTab userSnapshot={userSnapshot} />
        )}
        {activeTab === 'qualifications' && (
          <QualificationsTab qualifications={archive.qualifications_snapshot || []} validOnDate={validOnDate} />
        )}
      </div>
    </div>
  );
};

// Personal Data Tab Component
const PersonalDataTab = ({ userSnapshot }) => {
  const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon className="w-5 h-5 text-slate-400 mt-0.5" />}
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="text-slate-900">{value || 'N/A'}</p>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Basic Information</h3>
        <InfoRow label="Full Name" value={userSnapshot.full_name} icon={User} />
        <InfoRow label="Email" value={userSnapshot.email} icon={Mail} />
        <InfoRow label="Company Email" value={userSnapshot.company_email} icon={Mail} />
        <InfoRow label="Phone" value={userSnapshot.phone_number} icon={Phone} />
        <InfoRow label="Date of Birth" value={userSnapshot.date_of_birth} icon={Calendar} />
        <InfoRow label="PESEL" value={userSnapshot.pesel} />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Address & Location</h3>
        <InfoRow label="Personal Address" value={userSnapshot.personal_address} icon={MapPin} />
        <InfoRow label="Closest Airport" value={userSnapshot.closest_airport} />
        <InfoRow label="Rotation Weeks On" value={userSnapshot.rotation_weeks_on} />
        <InfoRow label="Rotation Weeks Off" value={userSnapshot.rotation_weeks_off} />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Business Details</h3>
        <InfoRow label="Company Name" value={userSnapshot.your_company_name} icon={Building} />
        <InfoRow label="Company Address" value={userSnapshot.company_address} />
        <InfoRow label="Tax ID (NIP)" value={userSnapshot.tax_id} />
        <InfoRow label="EU Tax ID" value={userSnapshot.eu_tax_id} />
        <InfoRow label="Finnish Tax ID" value={userSnapshot.tax_id_fi} />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">System IDs</h3>
        <InfoRow label="SAP ID" value={userSnapshot.sap_id} />
        <InfoRow label="WINDA ID" value={userSnapshot.winda_id} />
      </div>

      {userSnapshot.languages && userSnapshot.languages.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Languages</h3>
          <div className="space-y-2">
            {userSnapshot.languages.map((lang, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-slate-700">{lang.language}</span>
                <span className="bg-brand-100 text-brand-700 px-2 py-0.5 rounded text-sm font-medium">
                  {lang.level.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {userSnapshot.comment && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Comments</h3>
          <p className="text-slate-600 whitespace-pre-wrap">{userSnapshot.comment}</p>
        </div>
      )}
    </div>
  );
};

// Qualifications Tab Component
const QualificationsTab = ({ qualifications, validOnDate }) => {
  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '').replace('/api', '');
    return `${baseUrl}/media/${filePath}`;
  };

  // Format date string to DD/MM/YYYY
  const formatDateStr = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Get "Valid To" date object - the earlier of superseded_at or expiry_date
  const getValidToDate = (qual) => {
    const superseded = qual.superseded_at ? new Date(qual.superseded_at) : null;
    const expiry = qual.expiry_date ? new Date(qual.expiry_date) : null;

    if (superseded && expiry) {
      return superseded < expiry ? superseded : expiry;
    }
    return superseded || expiry || null;
  };

  // Get formatted "Valid To" string
  const getValidToStr = (qual) => {
    const superseded = qual.superseded_at ? new Date(qual.superseded_at) : null;
    const expiry = qual.expiry_date ? new Date(qual.expiry_date) : null;

    if (superseded && expiry) {
      return formatDateStr(superseded < expiry ? qual.superseded_at : qual.expiry_date);
    }
    if (superseded) return formatDateStr(qual.superseded_at);
    if (expiry) return formatDateStr(qual.expiry_date);
    return null;
  };

  // Filter by validOnDate if set
  const filterByDate = (quals) => {
    if (!validOnDate) return quals;

    const filterDate = new Date(validOnDate);
    filterDate.setHours(0, 0, 0, 0);

    return quals.filter(qual => {
      const validFrom = qual.created_at ? new Date(qual.created_at) : null;
      const validTo = getValidToDate(qual);

      if (!validFrom) return false;

      // Valid from must be <= filter date
      if (validFrom > filterDate) return false;

      // If there's a valid to date, it must be >= filter date
      if (validTo && validTo < filterDate) return false;

      return true;
    });
  };

  // Sort: alphabetically by qualification_type, then by version (descending)
  const sortedQualifications = [...qualifications].sort((a, b) => {
    const nameCompare = a.qualification_type.localeCompare(b.qualification_type);
    if (nameCompare !== 0) return nameCompare;
    return b.version - a.version; // Newest version first
  });

  const filteredQualifications = filterByDate(sortedQualifications);

  if (qualifications.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
        <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">No qualifications archived</p>
      </div>
    );
  }

  if (filteredQualifications.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
        <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">No qualifications valid on {formatDateStr(validOnDate)}</p>
        <p className="text-sm text-slate-400 mt-2">Total qualifications in archive: {qualifications.length}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {validOnDate && (
        <div className="mb-3 text-sm text-slate-600 flex-shrink-0">
          Showing {filteredQualifications.length} of {qualifications.length} qualifications valid on {formatDateStr(validOnDate)}
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col min-h-0 flex-1">
        {/* Fixed header */}
        <div className="flex-shrink-0 bg-slate-50 border-b border-slate-200 rounded-t-xl">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase w-[20%]">Qualification</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase w-[12%]">Category</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase w-[14%]">Certificate #</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase w-[12%]">Valid From</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase w-[12%]">Valid To</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase w-[10%]">Version</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase w-[10%]">File</th>
              </tr>
            </thead>
          </table>
        </div>
        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <table className="min-w-full">
            <tbody className="divide-y divide-slate-200">
              {filteredQualifications.map((qual, idx) => (
                <tr key={idx} className={`hover:bg-slate-50 ${!qual.is_current ? 'bg-slate-50/50 text-slate-500' : ''}`}>
                  <td className="px-4 py-3 font-medium w-[20%]">{qual.qualification_type}</td>
                  <td className="px-4 py-3 w-[12%]">{qual.category || 'N/A'}</td>
                  <td className="px-4 py-3 font-mono text-sm w-[14%]">{qual.certificate_number || 'N/A'}</td>
                  <td className="px-4 py-3 w-[12%]">{formatDateStr(qual.created_at) || 'N/A'}</td>
                  <td className="px-4 py-3 w-[12%]">
                    {getValidToStr(qual) || (qual.is_current ? <span className="text-green-600">Active</span> : 'N/A')}
                  </td>
                  <td className="px-4 py-3 w-[10%]">v{qual.version}</td>
                  <td className="px-4 py-3 w-[10%]">
                    {qual.certificate_file ? (
                      <div className="flex gap-1">
                        <a
                          href={getFileUrl(qual.certificate_file)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-brand-600 hover:bg-brand-50 rounded transition-colors"
                          title="View file"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <a
                          href={getFileUrl(qual.certificate_file)}
                          download
                          className="p-1.5 text-brand-600 hover:bg-brand-50 rounded transition-colors"
                          title="Download file"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-sm">No file</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ArchiveDetailView;
